import type { BadgeProps } from "@/components/ui";
import { MatterIncidentReportContextMenu } from "@/components/features/matter/context-menus/MatterIncidentReportContextMenu";
import { LinkedMatterMembershipPopover } from "@/components/features/matter/shared/LinkedMatterMembershipPopover";
import {
  ActionConfirmationModal,
  LoadingSpinner,
  Badge,
  Button,
  Card,
  CardHeader,
  ExpandableText,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Text,
} from "@/components/ui";
import { CompactContactPreviewChip } from "@/components/ui/ContactPreviewChip/CompactContactPreviewChip";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  useCreateMatterIncidentReport,
  useDeleteMatterIncidentReport,
} from "@/lib/data/react-query/matters/matter-incident-report";
import { useUpdateMatterIncidentReportNotApplicable } from "@/lib/data/react-query/matters/matter";
import type { MATTER_INCIDENT_REPORT_STATUS_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MatterIncidentReport } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  getIncidentReportConnectionContactName,
  getIncidentReportContactConnections,
} from "@/lib/utils/connections/incident-report-contact-connections";
import { formatTime } from "@/lib/utils/format";
import { IncidentIcon } from "@/lib/utils/iconUtils";
import { CirclePlus, MoreVertical, Pencil, Trash2 } from "lucide-react";
import pluralize from "pluralize";
import { useState, type KeyboardEvent } from "react";

const VISIBLE_SOURCE_COUNT = 2;

type IncidentReportStatus =
  (typeof MATTER_INCIDENT_REPORT_STATUS_OPTIONS)[number];

const incidentReportStatusVariantMap: Record<
  IncidentReportStatus,
  BadgeProps["variant"]
> = {
  "Not Requested": "outline",
  Requested: "warning",
  Received: "success",
};

const getIncidentReportSentimentVariant = (
  sentiment?: MatterIncidentReport["sentiment"],
): BadgeProps["variant"] => {
  if (sentiment === "Favorable") {
    return "success";
  }

  if (sentiment === "Unfavorable") {
    return "destructive";
  }

  if (sentiment === "Inconclusive") {
    return "warning";
  }

  return "outline";
};

export const MatterIncidentReports = ({ matter }: { matter: Matter }) => {
  const { hasActionPermission } = useUser();
  const { openIncidentReportModal } = useGlobalModal();
  const updateIncidentReportNotApplicable =
    useUpdateMatterIncidentReportNotApplicable();

  const createIncidentReport = useCreateMatterIncidentReport();
  const [isLegacyDeleteOpen, setIsLegacyDeleteOpen] = useState(false);
  const [pendingDeleteMarker, setPendingDeleteMarker] =
    useState<MatterIncidentReport | null>(null);
  const deleteMarker = useDeleteMatterIncidentReport({
    errorMessage: pendingDeleteMarker?.noReport
      ? "Error removing no incident report marker"
      : "Error removing not applicable incident report marker",
    successMessage: pendingDeleteMarker?.noReport
      ? "Removed no incident report marker"
      : "Removed not applicable incident report marker",
  });

  const hasViewPermission = Boolean(true);
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIncidentReport,
    ACTION_PERMISSION_VALUES.create,
  );
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIncidentReport,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIncidentReport,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canEditIncidentReport = hasUpdatePermission || hasDeletePermission;

  if (!hasViewPermission) {
    return (
      <Card className="w-full border-2">
        <CardHeader className="space-y-1">
          <Text variant="subHeading">
            You do not have permission to view incident reports.
          </Text>
        </CardHeader>
      </Card>
    );
  }

  const incidentReports = matter.incidentReports;
  const isNotApplicable =
    Boolean(matter.incidentReportNotApplicable) && incidentReports.length === 0;

  const handleToggleNotApplicable = (checked: boolean) => {
    if (!hasUpdatePermission || updateIncidentReportNotApplicable.isPending) {
      return;
    }

    if (checked === isNotApplicable) {
      return;
    }

    return updateIncidentReportNotApplicable.mutateAsync({
      matterId: matter.id,
      incidentReportNotApplicable: checked,
      dateModified: matter.dateModified,
    });
  };

  const getIncidentReportSourceConnections = (report: MatterIncidentReport) => {
    return getIncidentReportContactConnections(
      report,
      "Incident Report Source",
    );
  };

  const openReport = (reportId: string) => {
    openIncidentReportModal({
      matterId: matter.id,
      incidentReportId: reportId,
      editing: true,
      initialTab: "details",
    });
  };

  return (
    <div className="flex flex-col gap-3 p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <IncidentIcon className="text-primary h-4 w-4" />
          <Text variant="body" color="primary" className="font-semibold">
            Incident reports
          </Text>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {hasCreatePermission && (
            <div className="flex items-center">
              <Button
                size="sm"
                variant="outline"
                className="bg-background rounded-r-none border-r-0 pr-3"
                disabled={
                  createIncidentReport.isPending ||
                  isNotApplicable ||
                  incidentReports.some(
                    (report) => report.noReport || report.notApplicable,
                  )
                }
                onClick={() => {
                  openIncidentReportModal({
                    matterId: matter.id,
                    editing: false,
                    initialTab: "details",
                  });
                }}
              >
                <CirclePlus className="h-4 w-4 shrink-0" />
                Add incident report
              </Button>
              <span
                aria-hidden
                className="bg-border pointer-events-none relative z-[1] -mx-px h-7 w-px shrink-0"
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-background rounded-l-none px-2"
                    aria-label="Additional options"
                    disabled={createIncidentReport.isPending}
                  >
                    {createIncidentReport.isPending ? (
                      <LoadingSpinner className="h-4 w-4" />
                    ) : (
                      <MoreVertical className="h-4 w-4" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  side="bottom"
                  sideOffset={4}
                  className="z-[110] min-w-[12rem]"
                >
                  <DropdownMenuItem
                    disabled={
                      createIncidentReport.isPending ||
                      isNotApplicable ||
                      incidentReports.length > 0
                    }
                    onSelect={() => {
                      createIncidentReport.mutate({
                        matterId: matter.id,
                        noReport: true,
                        notApplicable: false,
                      });
                    }}
                  >
                    Mark no report
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={
                      createIncidentReport.isPending ||
                      isNotApplicable ||
                      incidentReports.length > 0
                    }
                    onSelect={() => {
                      createIncidentReport.mutate({
                        matterId: matter.id,
                        noReport: false,
                        notApplicable: true,
                      });
                    }}
                  >
                    Mark not applicable
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
      {isNotApplicable ? (
        <div className="bg-card flex items-center justify-between gap-3 rounded-2xl border px-4 py-3">
          <Text
            variant="body"
            className="font-semibold text-amber-700 dark:text-amber-300"
          >
            Incident report not applicable
          </Text>
          {hasUpdatePermission && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-red-600 hover:text-red-700"
              aria-label="Delete not applicable"
              disabled={updateIncidentReportNotApplicable.isPending}
              onClick={() => {
                setIsLegacyDeleteOpen(true);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : incidentReports.length === 0 ? (
        <Card className="w-full border-2 border-dashed">
          <CardHeader className="space-y-1 text-center">
            <Text
              variant="body"
              className="text-muted-foreground font-semibold"
            >
              {"You haven't uploaded an incident report yet."}
            </Text>
            <Text variant="subHeading">
              {"Upload your first report by clicking 'Add incident report'."}
            </Text>
          </CardHeader>
        </Card>
      ) : (
        <div className="flex flex-col gap-y-2">
          {incidentReports.map((report) => {
            if (report.noReport || report.notApplicable) {
              return (
                <div
                  key={report.id}
                  className="bg-card flex items-center justify-between gap-3 rounded-2xl border px-4 py-3"
                  data-testid="incident-report-card"
                >
                  <Text
                    variant="body"
                    className="font-semibold text-amber-700 dark:text-amber-300"
                  >
                    {report.noReport
                      ? "No Incident Report"
                      : "Incident Report Not Applicable"}
                  </Text>
                  {hasDeletePermission && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:text-red-700"
                      aria-label={`Delete ${report.noReport ? "No Report" : "Not Applicable"}`}
                      disabled={
                        createIncidentReport.isPending || deleteMarker.isPending
                      }
                      onClick={() => {
                        setPendingDeleteMarker(report);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            }
            const incidentReportSourceConnections =
              getIncidentReportSourceConnections(report);
            const visibleSources = incidentReportSourceConnections.slice(
              0,
              VISIBLE_SOURCE_COUNT,
            );
            const overflowSources =
              incidentReportSourceConnections.slice(VISIBLE_SOURCE_COUNT);
            const overflowSourceNames = overflowSources
              .map((connection) =>
                getIncidentReportConnectionContactName(connection.contact),
              )
              .join("\n");
            const description = report.description?.trim();
            const fileNumber = report.fileNumber?.trim();
            const time = formatTime(report.time ?? undefined);

            const handleRowKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openReport(report.id);
              }
            };

            return (
              <MatterIncidentReportContextMenu
                key={report.id}
                matter={matter}
                report={report}
                onOpen={() => {
                  openReport(report.id);
                }}
                trigger={
                  <Card
                    className="border-border hover:bg-muted/50 group w-full cursor-pointer transition-colors"
                    data-testid="incident-report-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      openReport(report.id);
                    }}
                    onKeyDown={handleRowKeyDown}
                  >
                    <div
                      className={cn(
                        "flex flex-col gap-2 px-4 py-3",
                        description ? "pb-2" : null,
                      )}
                    >
                      <div className="flex items-center gap-2 md:gap-4">
                        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                          <div className="flex shrink-0 items-center gap-1.5">
                            <Badge
                              variant={
                                report.status
                                  ? incidentReportStatusVariantMap[
                                      report.status
                                    ]
                                  : "outline"
                              }
                            >
                              {report.status}
                            </Badge>
                            {report.sentiment ? (
                              <Badge
                                variant={getIncidentReportSentimentVariant(
                                  report.sentiment,
                                )}
                              >
                                {report.sentiment}
                              </Badge>
                            ) : null}
                            <LinkedMatterMembershipPopover
                              currentMatterId={matter.id}
                              enabled
                              objectId={report.id}
                              objectType="incident-report"
                            />
                          </div>
                          <div
                            className="flex min-w-0 items-center gap-x-1.5 overflow-hidden"
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            <Text
                              variant="subHeading"
                              className="text-muted-foreground font-bold"
                            >
                              {`${pluralize(
                                "Source",
                                incidentReportSourceConnections.length,
                              )}:`}
                            </Text>
                            {incidentReportSourceConnections.length > 0 ? (
                              <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                                {visibleSources.map((connection) => (
                                  <CompactContactPreviewChip
                                    key={connection.id}
                                    contact={connection}
                                    matter={matter}
                                    className="min-w-0 shrink-0"
                                    textClassName="truncate max-w-[10rem] xl:max-w-[14rem]"
                                  />
                                ))}
                                {overflowSources.length > 0 ? (
                                  <TooltipWrapper message={overflowSourceNames}>
                                    <Badge
                                      variant="outline"
                                      className="text-muted-foreground shrink-0 text-xs"
                                    >
                                      +{overflowSources.length} more
                                    </Badge>
                                  </TooltipWrapper>
                                ) : null}
                              </div>
                            ) : (
                              <Text variant="body" className="font-semibold">
                                -
                              </Text>
                            )}
                          </div>
                        </div>

                        {canEditIncidentReport ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn(
                              "text-muted-foreground hover:text-foreground hover:bg-muted/70 focus-visible:bg-muted/70 h-8 w-8 shrink-0",
                              "opacity-100 transition-opacity md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100",
                            )}
                            onClick={(event) => {
                              event.stopPropagation();
                              openReport(report.id);
                            }}
                            data-testid="edit-incident-report-button"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>

                      {time || fileNumber ? (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                          {time ? (
                            <Text variant="caption" color="muted">
                              <span className="text-foreground font-medium">
                                Time:
                              </span>{" "}
                              {time}
                            </Text>
                          ) : null}
                          {fileNumber ? (
                            <Text variant="caption" color="muted">
                              <span className="text-foreground font-medium">
                                File number:
                              </span>{" "}
                              {fileNumber}
                            </Text>
                          ) : null}
                        </div>
                      ) : null}

                      {description ? (
                        <ExpandableText
                          text={description}
                          maxLines={2}
                          className="text-muted-foreground"
                        />
                      ) : null}
                    </div>
                  </Card>
                }
              />
            );
          })}
        </div>
      )}
      <ActionConfirmationModal
        open={pendingDeleteMarker !== null}
        setOpen={(open) => {
          if (!open) setPendingDeleteMarker(null);
        }}
        title={`Delete ${pendingDeleteMarker?.noReport ? "no report" : "not applicable"} marker`}
        description={`This will remove the ${pendingDeleteMarker?.noReport ? "no incident report" : "not applicable incident report"} marker for this matter.`}
        buttonText="Delete"
        isActionPending={deleteMarker.isPending}
        afterActionFn={async () => {
          if (!pendingDeleteMarker) return;
          await deleteMarker.mutateAsync({
            matterId: matter.id,
            id: pendingDeleteMarker.id,
          });
          setPendingDeleteMarker(null);
        }}
      />
      <ActionConfirmationModal
        open={isLegacyDeleteOpen}
        setOpen={setIsLegacyDeleteOpen}
        title="Delete not applicable marker"
        description="This will remove the not applicable incident report marker for this matter."
        buttonText="Delete"
        isActionPending={updateIncidentReportNotApplicable.isPending}
        afterActionFn={async () => {
          await handleToggleNotApplicable(false);
        }}
      />
    </div>
  );
};
